// Standalone module (not ./meeting): kept free of SDK imports so it stays
// unit-testable under vitest. Deep import: the ./navigation barrel re-exports
// redirect components (cycle risk).
import { getGlobalQueryLabel, NavigateGlobalQuery } from "./navigation/queries";

export interface MeetingReference {
  id: string;
  provider?: string;
}

// Path segments that prefix a meeting id in app URLs (see PAGE_PATHS:
// /lobby/:id, /j/:id, /meeting/:id[/...], /player/:id).
const MEETING_URL_ID_SEGMENTS = ["lobby", "j", "meeting", "player"];

/**
 * Extract a meeting reference from free-form user input: a raw id (mongo id or
 * short id) or a pasted app URL — with or without protocol, full or path-only,
 * with an optional `?provider=` query. Permissive by design: any bare token is
 * returned as-is, the lobby's accessibility endpoint stays the real validator.
 * Returns undefined when nothing id-shaped can be extracted.
 */
export const parseMeetingReference = (input: string): MeetingReference | undefined => {
  const raw = input.trim();
  if (!raw) return undefined;

  // Bare id (mongo id or short id): let the lobby resolve it.
  if (!raw.includes("/")) return { id: raw };

  // URL-shaped input. Absent an explicit protocol, a base URL makes both
  // "host/j/abc" and "/lobby/abc" parse as paths of a placeholder origin.
  let url: URL;
  try {
    url = raw.includes("://") ? new URL(raw) : new URL(raw, "https://placeholder");
  } catch {
    return undefined;
  }

  const segments = url.pathname.split("/").filter(Boolean);
  const index = segments.findIndex((segment) => MEETING_URL_ID_SEGMENTS.includes(segment));
  const id = index !== -1 ? segments[index + 1] : undefined;
  if (!id) return undefined;

  const provider = url.searchParams.get(getGlobalQueryLabel(NavigateGlobalQuery.PROVIDER)) ?? undefined;
  return { id, provider };
};
